/* eslint-disable spire/export-styles */
import { CustomState } from "@bToC/Store/Reducers";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, History, withHistory } from "@insite/mobius/utilities/HistoryContext";
import parse from "html-react-parser";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const imageWrapperStyles = {
    css: css`
        min-height: 100px;
        cursor: pointer;
    `,
};

const wrapperStyles = {
    css: css`
        text-align: center;
        justify-content: center;
        min-height: 100%;
        padding-top: 2rem;
        &:hover {
            background: "#016EAF";
        }
    `,
};

const titleStyles = {
    css: css`
        font-weight: bold;
        color: #016eaf;
    `,
};

const subTitleStyles = {
    css: css`
        margin-top: 1.5rem;
        margin-bottom: 2rem;
    `,
};

const moreBtnStyles = {
    css: css`
        color: white;
        margin-bottom: 1rem;
        min-width: 200px;
    `,
};

const contactBtnStyles = {
    css: css`
        color: #f06907;
        min-width: 200px;
    `,
};

const enum fields {
    extraLargeColumnCount = "extraLargeColumnCount",
    extraLargeRowCount = "extraLargeRowCount",
    largeColumnCount = "largeColumnCount",
    mediumColumnCount = "mediumColumnCount",
    smallColumnCount = "smallColumnCount",
    extraSmallColumnCount = "extraSmallColumnCount",

    solutionLink = "solutionLink",
    solutionIcon = "solutionIcon",
    title = "title",
    subTitle = "subTitle",
    altText = "altText",
    solutionType = "solutionType",
    solutions = "solutions",
    linkUrl = "linkUrl",
}

interface SolutionModel {
    fields: {
        solutionLink: string;
        solutionIcon: string;
        title: string;
        subTitle: string;
        altText: string;
        solutionType: string;
        link: string;
        linkUrl: LinkFieldValue;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.extraLargeColumnCount]: GridWidths;
        [fields.extraLargeRowCount]: GridWidths;
        [fields.largeColumnCount]: GridWidths;
        [fields.mediumColumnCount]: GridWidths;
        [fields.smallColumnCount]: GridWidths;
        [fields.extraSmallColumnCount]: GridWidths;
        [fields.solutions]: SolutionModel[];
    };
}

type Props = HasHistory & OwnProps & ReturnType<typeof mapStateToProps>;

const getColumnGridUnits = (numberOfColumns: number) => {
    return Math.floor(12 / Math.min(Math.max(numberOfColumns, 1), 12)) as GridWidths;
};

const onClick = (history: History, link: string | undefined) => {
    if (link) {
        history.push(link);
    }
};

const solutionsGrid: React.FC<Props> = ({ fields, history, id, solutionsArr }) => {
    const extraLargeColumnWidth = getColumnGridUnits(fields.extraLargeColumnCount);
    const largeColumnWidth = getColumnGridUnits(fields.largeColumnCount);
    const mediumColumnWidth = getColumnGridUnits(fields.mediumColumnCount);
    const smallColumnWidth = getColumnGridUnits(fields.smallColumnCount);
    const extraSmallColumnWidth = getColumnGridUnits(fields.extraSmallColumnCount);

    const { solutions } = fields;

    const gridStyles = {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            max-width: 100%;
                        `,
                    ],
                    "min",
                )};
        `,
    };

    const rows: JSX.Element[] = [];
    let counter = 0;

    let filteredsolutions: Array<SolutionModel>;

    if (solutionsArr.length === 0) {
        filteredsolutions = solutions;
    } else {
        // arr1.some(item => arr2.includes(item))
        filteredsolutions = solutions.filter(obj => {
            const tempTypeArr = obj.fields.solutionType.split(", ");
            return solutionsArr.some(i => tempTypeArr.includes(i));
            // solutionsArr.includes(obj.fields.solutionType)
        });
    }

    for (let row = 0; row < fields.extraLargeRowCount; row = row + 1) {
        const columns: JSX.Element[] = [];
        for (let column = 0; column < fields.extraLargeColumnCount; column = column + 1) {
            if (counter > filteredsolutions.length - 1) {
                break;
            }

            const { solutionType, solutionLink, solutionIcon, title, subTitle, altText, linkUrl } = filteredsolutions[
                counter
            ].fields;

            const currentUrl = linkUrl ? useGetLink(linkUrl) : linkUrl;

            const currentLink = currentUrl?.url || `/solutions/${title.replace(/\s/g, "")}`;
            columns.push(
                <GridItem
                    key={`GridItem-${id}-${column}-${row}`}
                    width={[
                        extraSmallColumnWidth,
                        smallColumnWidth,
                        mediumColumnWidth,
                        largeColumnWidth,
                        extraLargeColumnWidth,
                    ]}
                >
                    <StyledWrapper {...wrapperStyles}>
                        <StyledWrapper {...imageWrapperStyles}>
                            <LazyImage
                                src={solutionIcon}
                                altText={altText}
                                onClick={() => onClick(history, currentLink)}
                            />
                        </StyledWrapper>

                        <Typography {...titleStyles}>{title}</Typography>

                        <StyledWrapper {...subTitleStyles}>
                            <Typography>{parse(subTitle, parserOptions)}</Typography>
                        </StyledWrapper>

                        <StyledWrapper>
                            <Button color="#F06907" {...moreBtnStyles} onClick={() => onClick(history, currentLink)}>
                                More Information
                            </Button>
                        </StyledWrapper>
                        <StyledWrapper>
                            <Button
                                color="#F06907"
                                buttonType="outline"
                                {...contactBtnStyles}
                                onClick={() => onClick(history, "/contactus")}
                            >
                                {" "}
                                Contact Gerrie{" "}
                            </Button>
                        </StyledWrapper>
                    </StyledWrapper>
                </GridItem>,
            );

            counter++;
        }

        rows.push(
            <GridContainer {...gridStyles} key={row} gap={0}>
                {columns}
            </GridContainer>,
        );
    }

    return <>{rows}</>;
};

const contentTab = {
    displayName: "Content",
    sortOrder: 1,
};

const mapStateToProps = (state: CustomState) => ({
    solutionsArr: state.addSolutionsReducer.solutions,
});

const gridModule: WidgetModule = {
    component: connect(mapStateToProps)(withHistory(solutionsGrid)),
    definition: {
        group: "Basic",
        icon: "Grid",
        fieldDefinitions: [
            {
                name: fields.extraLargeColumnCount,
                displayName: "Extra Large Column Count",
                editorTemplate: "IntegerField",
                defaultValue: 4,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.extraLargeRowCount,
                displayName: "Extra Large Row Count",
                editorTemplate: "IntegerField",
                defaultValue: 4,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.largeColumnCount,
                displayName: "Large Width Column Count",
                editorTemplate: "IntegerField",
                defaultValue: 4,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.mediumColumnCount,
                displayName: "Medium Width Column Count",
                editorTemplate: "IntegerField",
                defaultValue: 1,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.smallColumnCount,
                displayName: "Small Width Column Count",
                editorTemplate: "IntegerField",
                defaultValue: 1,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.extraSmallColumnCount,
                displayName: "Extra Small Width Column Count",
                editorTemplate: "IntegerField",
                defaultValue: 1,
                fieldType: "General",
                sortOrder: 6,
            },

            {
                name: fields.solutions,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: ({ fields: { title } }) => title,
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: fields.solutionType,
                        editorTemplate: "TextField",
                        displayName: "solution Type - for multi types (e.g. test1, test2)",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: fields.solutionIcon,
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: fields.title,
                        editorTemplate: "TextField",
                        displayName: "solution Title",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: fields.subTitle,
                        editorTemplate: "RichTextField",
                        displayName: "Sub Title",
                        defaultValue: "",
                        extendedConfig: { height: 100 },
                    },
                    {
                        name: fields.altText,
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: fields.linkUrl,
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                    },
                ],
            },
        ],
    },
};

export default gridModule;
